import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import { ChatsAPI } from "../chatsApi";
import {
  ChatStatus,
  ChatStatusFilter,
  IChat,
  IFulfilledChat,
  TMessage,
  TUserShort
} from "../../interfaces/interfacesChat";
import { UserAPI } from "../userApi";
import {
  createFulfilledChats,
  getChatPartnerId,
  sortMessagesFunction
} from "../../utils/chat";
import { getMatchQueryKey } from "./studentApiQueries";
import { useMemo } from "react";

export const getChatMessagesQueryKey = ({ chatId }: { chatId?: string }) =>
  ["chatMessages", chatId].filter((s) => Boolean(s));

export const useGetChatMessages = ({
  chatId,
  onSuccess
}: {
  chatId?: string;
  onSuccess?: (data: InfiniteData<{ messages: TMessage[] }>) => void;
}) => {
  return useInfiniteQuery(
    getChatMessagesQueryKey({ chatId }),
    async ({
      pageParam: beforeDt
    }): Promise<{
      messages: TMessage[];
    }> => {
      if (!chatId || beforeDt === false) {
        return {
          messages: []
        };
      }
      const { messages } = await ChatsAPI.getChatMessages(chatId, beforeDt);

      return {
        messages: messages.sort(sortMessagesFunction)
      };
    },
    {
      enabled: Boolean(chatId),
      refetchOnMount: "always",
      getPreviousPageParam: (firstPage) => {
        return firstPage.messages?.[0]?.created_at ?? undefined;
      },
      onSuccess: onSuccess
    }
  );
};

export const searchChatsQueryKey = ({
  userId,
  filter,
  chatStatus
}: {
  userId?: string;
  filter?: string;
  chatStatus?: string;
}) => ["chats", userId, filter, chatStatus].filter((s) => Boolean(s));

export const useSearchChats = ({
  userId,
  userType,
  chatStatus,
  filter,
  enabled = true
}: {
  userId?: string;
  userType?: string;
  chatStatus?: ChatStatus;
  filter?: string;
  enabled?: boolean;
}) => {
  return useInfiniteQuery(
    searchChatsQueryKey({ userId, filter, chatStatus }),
    async ({
      pageParam: nextBatchToken
    }): Promise<{
      chats: IFulfilledChat[];
      nextBatchToken: string | null;
    }> => {
      if (!userId || !userType || !chatStatus) {
        return {
          chats: [],
          nextBatchToken: null
        };
      }

      const searchResult = filter
        ? await ChatsAPI.searchUserChats(
            userId,
            filter,
            chatStatus,
            nextBatchToken
          )
        : await ChatsAPI.getUserChats(
            userId,
            chatStatus as unknown as ChatStatusFilter, // TODO: dirty fix
            100,
            nextBatchToken
          );

      const {
        chats,
        next_batch_token
      }: { chats: IChat[]; next_batch_token: string | null } = searchResult;

      const userIds = Array.from(
        new Set(chats.map((chat) => getChatPartnerId(chat, userType)))
      );

      if (userIds.length === 0) {
        return {
          chats: [],
          nextBatchToken: null
        };
      }

      const {
        users_short_description: usersDescription
      }: {
        users_short_description: TUserShort[];
      } = await UserAPI.getShortDescription(userIds);

      return {
        chats: createFulfilledChats(chats, usersDescription, userType),
        nextBatchToken: next_batch_token
      };
    },
    {
      enabled: Boolean(userId) && Boolean(userType) && enabled,
      getNextPageParam: (lastPage) => {
        return lastPage.nextBatchToken ?? undefined;
      },
      refetchOnMount: "always"
    }
  );
};

export const getChatCountersQueryKey = () => ["chatCounters"];
export const useGetChatCounters = () => {
  return useQuery(
    getChatCountersQueryKey(),
    () => {
      return ChatsAPI.getUserCounters();
    },
    {
      refetchOnMount: "always"
    }
  );
};

export const getUserOnlineStatusQueryKey = (userId?: string) => [
  "userOnlineStatus",
  userId
];
export const useGetUserOnlineStatus = (userId?: string) => {
  return useQuery(
    getUserOnlineStatusQueryKey(userId),
    () => {
      if (!userId) {
        return;
      }
      return ChatsAPI.getUserOnlineStatus(userId);
    },
    {
      enabled: Boolean(userId),
      refetchOnMount: "always",
      refetchInterval: 5000
    }
  );
};

export const useReportChatAbuseMutation = ({
  onSuccess
}: {
  onSuccess?: () => void;
}) => {
  return useMutation<
    unknown,
    unknown,
    { chatId: string; reporterId: string; reporteeId: string }
  >({
    mutationFn: async (vars) => {
      return ChatsAPI.reportChat(vars.chatId, vars.reporterId, vars.reporteeId)
        .then(() => null)
        .catch((e) => {
          if (e.status === 400) {
            return null;
          }
          return Promise.reject(e);
        });
    },
    onSuccess: onSuccess
  });
};

export const useAcceptChat = () => {
  const queryClient = useQueryClient();

  return useMutation<unknown, unknown, { chatId: string }>({
    mutationFn: async (vars) => {
      return ChatsAPI.acceptChat(vars.chatId).then(() => {
        queryClient.invalidateQueries({ queryKey: searchChatsQueryKey({}) });
        queryClient.invalidateQueries({
          queryKey: getChatMessagesQueryKey({})
        });
        queryClient.invalidateQueries({
          queryKey: getChatCountersQueryKey()
        });
        return null;
      });
    }
  });
};

export const useArchiveChat = () => {
  const queryClient = useQueryClient();

  return useMutation<
    unknown,
    unknown,
    { chatId: string; payload?: { message: string } }
  >({
    mutationFn: async (vars) => {
      return ChatsAPI.archive(vars.chatId, vars.payload).then(() => {
        queryClient.invalidateQueries({ queryKey: searchChatsQueryKey({}) });
        queryClient.invalidateQueries({
          queryKey: getChatMessagesQueryKey({})
        });
        queryClient.invalidateQueries({
          queryKey: getChatCountersQueryKey()
        });
        return null;
      });
    }
  });
};

export const useUnArchiveChat = () => {
  const queryClient = useQueryClient();

  return useMutation<unknown, unknown, { chatId: string }>({
    mutationFn: async (vars) => {
      return ChatsAPI.unArchive(vars.chatId).then(() => {
        queryClient.invalidateQueries({ queryKey: searchChatsQueryKey({}) });
        queryClient.invalidateQueries({
          queryKey: getChatMessagesQueryKey({})
        });
        queryClient.invalidateQueries({
          queryKey: getChatCountersQueryKey()
        });
        return null;
      });
    }
  });
};

export const useInvalidateChatMatch = () => {
  const queryClient = useQueryClient();

  return useMemo(() => {
    return (chatId: string) => {
      const chatsCache = queryClient.getQueriesData({
        queryKey: searchChatsQueryKey({})
      });

      const allChats: IFulfilledChat[] = chatsCache
        .flatMap((c) => {
          // @ts-ignore
          return c?.[1]?.pages?.flatMap((p) => p?.chats);
        })
        .filter((c) => c);

      const chat = allChats.find((c) => c.chat_id === chatId);

      if (chat) {
        queryClient.invalidateQueries({
          queryKey: getMatchQueryKey({
            studentId: chat.student_id,
            hostId: chat.host_id,
            houseId: chat.house_id
          })
        });
      }
    };
  }, [queryClient]);
};
